import { SetStateAction, useCallback, useState } from 'react';

import { useRentals, useRentalsSettings } from '@kaltura-ott/tvpil-shared';
import { RentalAsset } from '@kaltura-ott/tvpil-shared/lib/rhino/data/rental/types';

import { RailProviderData, RailSettings } from 'components/widgets/Rail/components/RailProvider/types';
import { useHandleSkeletonRef } from 'components/widgets/Rail/hooks/useHandleSkeletonRef/useHandleSkeletonRef';
import { DEFAULT_IMAGE_PARAMS, WIDGET_TYPE } from 'consts';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';

/**
 * `useRentalsRail` is a custom React hook used for retrieving and managing data necessary
 * for displaying a rentals rail in a web application. This hook combines rentals settings,
 * channel data, and rail configuration.
 *
 * @param referenceId - A unique identifier of the rentals rail widget.
 * @param pageReferenceId - A unique identifier of the page containing the rentals rail widget.
 *
 * @returns An object containing:
 * - `settings`: Configurations and settings for the retals component's UI, obtained from `useRentalsSettings`.
 * - `railData`: An object structured as `RailProviderData<RentalAsset>`, containing the RentalAsset,
 *               total count, and loading state.
 * - `railType`: Set to `WIDGET_TYPE.RENTAL`
 *               in this context, marking it as a rental-type rail component.
 */

function useRentalsRail(referenceId: string) {
  const settings = useRentalsSettings({
    referenceId,
    ...DEFAULT_IMAGE_PARAMS
  });

  const data = useRentals({
    referenceId,
    pageSize: 50, // 50 should be changed to RAIL_PAGE_SIZE (20) when graphql bug
    useLimit: true,
  });

  const railType = WIDGET_TYPE.RENTAL;
  const isLoading = data.isLoading && !data.items.length;
  const isRailHidden = (!data.isLoading && !data.items.length) || !!data.error;

  const [shouldPreventDisplay, setShouldPreventDisplay] = useState(true);
  const setShouldPreventDisplayForward = useCallback(
    (value: SetStateAction<boolean>) => setShouldPreventDisplay(value),
    [],
  );

  const railData: RailProviderData<RentalAsset> = {
    items: data.items,
    totalCount: data.totalCount,
    hasNextPage: data.hasNextPage,
    isLoading: data.isLoading,
    error: data.error,
    fetchNextPage: data.fetchNextPage,
    refetch: data.refetch,
  };

  const railSettings: RailSettings = {
    branding: settings?.branding,
    id: settings?.id,
    imageHeight: settings?.imageHeight,
    imageType: settings?.imageType,
    imageWidth: settings?.imageWidth,
    title: settings?.title,
    shouldShowRating: settings?.shouldShowRating,
    moreLink: settings?.moreLink,
  };

  const handleSkeletonRef = useHandleSkeletonRef(setShouldPreventDisplayForward, data.items, railSettings);

  useRhinoErrorReaction({ error: data.error, refetch: data.refetch });

  return {
    railData,
    railType,
    isLoading,
    isRailHidden,
    railSettings: settings ? railSettings : null,
    handleSkeletonRef,
    shouldPreventDisplay,
  };
}

export { useRentalsRail };
